// Helpers PUROS del modo "Prompt libre" — ADR-2026-09-07-modo-prompt-libre.
//
// El modo es el camino directo de imagen de "Crear desde cero" en una pantalla propia:
// un prompt, referencias opcionales (producto / modelo / imagen subida) y el pie de Modelo
// (Calidad · Formato / cantidad + Generar). Toda la lógica que se puede probar sin React
// vive acá; los componentes sólo la consumen.
//
// Sin LLM y sin `useV2Chat`: los adjuntos son `ChatAttachment` sólo para reusar los
// pickers del asistente tal cual, no porque haya un chat detrás.

import type { ChatAttachment } from '../../agentAttachments';
import { FORMAT_OPTIONS_V2 } from '../../mockData';
import { ratioDeFormato } from '../../../../utils/ratioDeFormato';
import { clampCantidad } from '../asistente/directMode';
import {
  creditosPorResolucion,
  type ResolucionImagen,
} from '../../../../lib/resolucionImagen';
import {
  priorizarReferencias,
  type ReferenciaEtiquetada, tipoDeReferenciaParaElMotor,
  ReferenciaIrrecuperable,
} from '../../referenciasDeGeneracion';

export { MAX_CANTIDAD, clampCantidad } from '../asistente/directMode';

/** Tope de referencias que acepta el motor (el mismo que la card del chat). Vive en
 *  `referenciasDeGeneracion`, que también decide QUÉ se recorta
 *  (ADR-2026-09-08-diez-referencias-y-recorte-por-prioridad). */
export { MAX_REFERENCIAS } from '../../referenciasDeGeneracion';

/** Proporción de respaldo cuando el formato no se reconoce: 4:5, la de la app. */
const RATIO_RESPALDO = 0.8;

/**
 * Créditos que va a costar la tanda, para la pastilla del Generar y el guard de saldo.
 *
 * Es la misma cuenta que Modelo: imágenes × multiplicador de la resolución
 * (ADR-2026-08-25-resolucion-de-generacion-2k-4k). La cantidad se acota al rango del
 * motor ANTES de multiplicar: el botón y el backend tienen que cobrar lo mismo.
 */
export function creditosPromptLibre(
  cantidad: number,
  calidad: ResolucionImagen | null | undefined,
): number {
  return clampCantidad(cantidad) * creditosPorResolucion(calidad);
}

/**
 * Qué falta para poder generar, o `undefined` si no falta nada. Es el texto que el pie
 * dibuja debajo del botón apagado.
 *
 * `listo` = la cuenta ya resolvió usuario y empresa. Sin eso no hay a quién cobrarle ni
 * dónde escribir la fila, así que el botón espera.
 */
export function faltaPromptLibre(args: {
  prompt: string;
  listo: boolean;
}): string | undefined {
  if (!args.listo) return 'Cargando tu cuenta…';
  if (!args.prompt.trim()) return 'Escribí qué querés generar';
  return undefined;
}

/**
 * Proporción ancho/alto del formato ELEGIDO (por su slug) — para los recuadros vacíos
 * de la tanda.
 *
 * Sale del nombre de la opción ("Posteo (4:5)"), que es donde el catálogo de formatos
 * guarda la proporción; es el mismo truco de `formatShortLabel`. El "A:B" lo lee
 * `utils/ratioDeFormato`, que es lo que usan el visor y Creaciones para el mismo
 * número: acá sólo se llega al texto. Un formato desconocido cae a 4:5.
 */
export function ratioDelFormatoElegido(formatSlug: string | null | undefined): number {
  const opt = FORMAT_OPTIONS_V2.find((f) => f.id === formatSlug);
  const m = opt?.name.match(/\((\d+:\d+)\)/);
  return ratioDeFormato(m?.[1]) ?? RATIO_RESPALDO;
}

/**
 * Lo que la resolución de referencias necesita del mundo, inyectado para poder
 * probarla sin red:
 *
 *   • `productoUrl` — la foto FULL del producto (el thumbnail de 300px del catálogo es
 *     pobre como referencia de fidelidad, ADR-586); `null` si no hay.
 *   • `modeloUrl`  — la foto de presentación del modelo guardado; `null` si no hay.
 *   • `adjuntoUrl` — la firma de una imagen subida (`storagePath` + bucket).
 */
export interface ResolverReferenciasDeps {
  productoUrl: (productId: string) => Promise<string | null>;
  modeloUrl: (modelId: string) => Promise<string | null> | string | null;
  adjuntoUrl: (storagePath: string, bucket?: string) => Promise<string | null>;
}

/**
 * Adjuntos → URLs de referencia para `generateFreeformImage`.
 *
 * Mismas reglas que el modo directo del asistente (`directMode.referencesFromAttachments`
 * + `FreeformGenerateCard`): sin repetidos y hasta `MAX_REFERENCIAS`, pero el recorte es
 * POR PRIORIDAD y no por orden de llegada — si sobran, cae el fondo antes que el producto
 * (ADR-2026-09-08-diez-referencias-y-recorte-por-prioridad). Un producto sin foto full cae
 * a la miniatura del chip; un modelo sólo entra si es del catálogo (`mode: 'preset'`): los
 * subidos/personalizados no tienen una foto que el motor pueda usar. Una referencia que
 * falla al resolver se saltea con aviso y no tira las demás.
 *
 * `max` es el tope de la RUTA que va a recibir estas referencias: imagen acepta 10 y
 * video 6, y el recorte tiene que pasar en la pantalla —donde se sabe qué es cada una y
 * se puede tirar el fondo antes que el producto— y no en la validación del backend, que
 * sólo puede rechazar el pedido entero
 * (ADR-2026-09-12-el-video-recortaba-en-la-ruta-y-no-en-la-pantalla).
 */
export async function resolverReferencias(
  adjuntos: ChatAttachment[],
  deps: ResolverReferenciasDeps,
  max?: number,
): Promise<ReferenciaEtiquetada[]> {
  const refs: ReferenciaEtiquetada[] = [];
  for (const att of adjuntos) {
    try {
      if (att.kind === 'product') {
        const full = await deps.productoUrl(att.productId);
        const u = full || att.imageUrl;
        if (u) refs.push({ url: u, tipo: 'producto', productId: att.productId, adjuntoId: att.id });
      } else if (att.kind === 'model') {
        const sel = att.selection;
        if (sel.mode === 'preset' && sel.id != null) {
          const u = (await deps.modeloUrl(String(sel.id))) || att.imageUrl;
          if (u) refs.push({ url: u, tipo: 'modelo', adjuntoId: att.id });
        }
      } else if (att.kind === 'image') {
        const u = await deps.adjuntoUrl(att.storagePath, att.bucket);
        if (u) refs.push({ url: u, tipo: 'adjunto', adjuntoId: att.id });
      } else if (att.kind === 'referencia') {
        if (att.imageUrl) refs.push({ url: att.imageUrl, tipo: tipoDeReferenciaParaElMotor(att.tipo), adjuntoId: att.id });
      }
    } catch (err) {
      // La que el usuario eligió y sin la cual la imagen saldría mal y cobrada igual (la
      // foto del modelo) frena todo; el resto se saltea con aviso
      // (ADR-2026-10-06-sin-la-foto-del-modelo-no-se-genera).
      if (err instanceof ReferenciaIrrecuperable) throw err;
      console.warn('[PromptLibre] referencia no resuelta', att.kind, err);
    }
  }
  return priorizarReferencias(refs, max);
}

/**
 * En qué ORDEN viajarían las referencias de estos adjuntos, sin resolver ninguna URL: las
 * mismas reglas de entrada y la misma prioridad que `resolverReferencias`, con una URL de
 * mentira por adjunto. Sirve para traducir `@ImageN` a nombres cuando todavía no se pidió
 * nada (el agente deja un prompt en la caja de Redes,
 * ADR-2026-10-04-las-menciones-de-video-se-ven-por-nombre). Lo único que no puede saber es
 * si dos adjuntos distintos terminan siendo la misma foto; el pedido real sí lo sabe y
 * manda con su propio orden.
 */
export function ordenDeReferencias(adjuntos: ChatAttachment[], max?: number): ReferenciaEtiquetada[] {
  const refs: ReferenciaEtiquetada[] = [];
  for (const att of adjuntos) {
    const url = `adjunto:${att.id}`;
    if (att.kind === 'product') refs.push({ url, tipo: 'producto', productId: att.productId, adjuntoId: att.id });
    else if (att.kind === 'model') {
      if (att.selection.mode === 'preset' && att.selection.id != null) refs.push({ url, tipo: 'modelo', adjuntoId: att.id });
    } else if (att.kind === 'image') refs.push({ url, tipo: 'adjunto', adjuntoId: att.id });
    else if (att.kind === 'referencia') {
      if (att.imageUrl) refs.push({ url, tipo: tipoDeReferenciaParaElMotor(att.tipo), adjuntoId: att.id });
    }
  }
  return priorizarReferencias(refs, max);
}
